import type { CockpitResponse, CockpitParams } from "@/types/cockpit";

const API_BASE = import.meta.env.VITE_API_BASE || "";

function formatExportValue(value: number, unit: string): string {
  switch (unit) {
    case "currency":
      return (value / 100).toFixed(2);
    case "percent":
      return value.toFixed(1);
    default:
      return String(value);
  }
}

export function escapeCsvField(field: string): string {
  const dangerous = ["=", "+", "-", "@", "\t", "\r"];
  if (dangerous.some((c) => field.startsWith(c))) {
    field = "'" + field;
  }
  if (field.includes(",") || field.includes('"') || field.includes("\n") || field.includes("'")) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export function exportCockpitCSV(data: CockpitResponse, metricName: string): void {
  const headers = ["Period", "Value", "Previous Year", "Change %", "Cumulative"];
  const rows = data.periods.map((p) => {
    const changePercent =
      p.previous_value !== 0
        ? (((p.value - p.previous_value) / p.previous_value) * 100).toFixed(1)
        : "–";
    return [
      p.period,
      formatExportValue(p.value, data.unit),
      formatExportValue(p.previous_value, data.unit),
      changePercent,
      formatExportValue(p.cumulative, data.unit),
    ];
  });

  const csvContent = [
    headers.map(escapeCsvField).join(","),
    ...rows.map((row) => row.map(escapeCsvField).join(",")),
  ].join("\n");

  const scopeLabel =
    data.scope.group_name ||
    data.scope.childcare_name ||
    "org";

  const from = data.periods[0]?.period || "unknown";
  const to = data.periods[data.periods.length - 1]?.period || "unknown";
  const filename = `${metricName}_${from}-${to}_${scopeLabel}.csv`;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// --- Generic list-table exporters (client-side) ---------------------------
//
// The cockpit metric exporters above are tied to the `CockpitResponse` shape
// (server XLSX, period series). The analytics explorer renders arbitrary
// verification tables, so it needs column-driven exporters over plain rows.

/** A single export column: how to read and label one cell from a row. */
export interface ExportColumn<T> {
  /** Stable key (also the XLSX header fallback). */
  key: string;
  /** Human header (column title). */
  header: string;
  /** Extracts the cell value for a row. */
  accessor: (row: T) => string | number | boolean | null | undefined;
}

function cellToString(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exports arbitrary rows to a CSV file (dependency-free). Headers and cells are
 * sanitized via {@link escapeCsvField} (formula-injection safe).
 */
export function exportRowsCSV<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  filename: string,
): void {
  const headerLine = columns.map((c) => escapeCsvField(c.header)).join(",");
  const dataLines = rows.map((row) =>
    columns
      .map((c) => escapeCsvField(cellToString(c.accessor(row))))
      .join(","),
  );
  const csvContent = [headerLine, ...dataLines].join("\n");
  triggerDownload(
    new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
    filename.endsWith(".csv") ? filename : `${filename}.csv`,
  );
}

/**
 * Exports arbitrary rows to an XLSX file using the pure-JS `write-excel-file`
 * library (browser build, lazily imported so it stays out of the main bundle
 * and is easy to mock in tests). All cells are emitted as strings for
 * predictability. The v4 browser API returns `{ toFile }` rather than
 * auto-downloading.
 */
export async function exportRowsXLSX<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  filename: string,
  sheetName = "Sheet1",
): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const headerRow = columns.map((c) => ({ value: c.header, type: String }));
  const dataRows = rows.map((row) =>
    columns.map((c) => ({ value: cellToString(c.accessor(row)), type: String })),
  );
  const data = [headerRow, ...dataRows];
  await writeXlsxFile(data, { sheet: sheetName }).toFile(
    filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`,
  );
}

/** One worksheet of a multi-sheet export. */
export interface ExportSheet {
  name: string;
  rows: unknown[];
  columns: ExportColumn<never>[];
}

/** A sheet from typed rows and columns (keeps the call sites type-checked). */
export function exportSheet<T>(name: string, rows: T[], columns: ExportColumn<T>[]): ExportSheet {
  return { name, rows, columns: columns as unknown as ExportColumn<never>[] };
}

/**
 * Exports several tables into one XLSX file, one worksheet each (#2175 E:
 * Stammdaten). Same cell rules as {@link exportRowsXLSX}; Excel caps a sheet
 * name at 31 characters.
 */
export async function exportSheetsXLSX(sheets: ExportSheet[], filename: string): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const data = sheets.map((sheet) => ({
    sheet: sheet.name.slice(0, 31),
    data: [
      sheet.columns.map((c) => ({ value: c.header, type: String })),
      ...sheet.rows.map((row) =>
        sheet.columns.map((c) => ({ value: cellToString((c.accessor as (r: unknown) => ReturnType<typeof c.accessor>)(row)), type: String })),
      ),
    ],
  }));
  await writeXlsxFile(data as never).toFile(filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

export async function exportCockpitXLSX(
  section: string,
  metric: string,
  params: CockpitParams,
  metricName: string,
  token: string,
): Promise<void> {
  const searchParams = new URLSearchParams();
  searchParams.set("format", "xlsx");
  searchParams.set("from", params.from);
  searchParams.set("to", params.to);
  if (params.granularity) searchParams.set("granularity", params.granularity);
  if (params.compare_to) searchParams.set("compare_to", params.compare_to);
  if (params.childcare_id) searchParams.set("childcare_id", params.childcare_id);
  if (params.group_id) searchParams.set("group_id", params.group_id);

  const url = `${API_BASE}/api/v1/cockpit/${section}/${metric}/export?${searchParams.toString()}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  const blob = await response.blob();
  const scopeLabel = params.group_id || params.childcare_id || "org";
  const filename = `${metricName}_${params.from}-${params.to}_${scopeLabel}.xlsx`;

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
